import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';

const API_BASE = 'https://visionry-api-production.up.railway.app';

const TZ = 'Europe/Dublin';
const DAY_KEYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']; // Monday-start
const DAY_LABELS = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' };

// ── Brand tokens (match visionry.io) ──
const C = {
  bg:       '#061618',
  card:     '#0d3338',
  card2:    '#10434a',
  border:   '#10434a',
  borderHi: '#1d8a9a',
  teal:     '#25a2b3',
  tealSoft: '#5fc4d2',
  tealMid:  '#1d8a9a',
  text:     '#e0f0f2',
  muted:    '#7fb8c0',
  green:    '#00cc88',
  greenBg:  '#06231a',
  red:      '#ff6b6b',
  redBorder:'#a13b3b',
  redBg:    '#2a0f0f',
  amber:    '#ffaa44',
  // EDT lesson block (teal-tinted now, not purple)
  edtBg:    '#0a2f44',
  edtBorder:'#25a2b3',
  lesBg:    '#0e3a40',
  lesBorder:'#1d8a9a',
  todayBg:  '#0a3a42',
  offBg:    '#081f22'
};

const REASONS = ['Holiday', 'Sick', 'Personal', 'Other'];

// Mobile breakpoint — matches Students.js.
const MOBILE_MAX = 640;
function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false);
  useEffect(() => {
    const onR = () => setM(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  return m;
}

// Shared action button — same 44px touch-target pattern as Students.js.
// `fill` shares a row equally (flex:1), `full` takes the whole row (width:100%).
// `filled` gives a solid `bg`/`color` pill instead of the outline style —
// for buttons that were already solid (primary actions / settled states).
function Btn({ children, onClick, color = C.muted, border = C.border, bg, disabled, fill, full, filled, type }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: fill ? 1 : 'none',
        width: full ? '100%' : undefined,
        boxSizing: 'border-box',
        background: filled ? bg : 'none',
        border: filled ? 'none' : `1px solid ${border}`,
        borderRadius: 10,
        color, fontSize: 14, fontWeight: 600, padding: '11px 16px',
        minHeight: 44, whiteSpace: 'nowrap',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1
      }}
    >
      {children}
    </button>
  );
}

// Segmented toggle — same pattern as Students.js SegBtn, with a configurable
// active colour (Diary uses amber for time-off reasons, teal for lesson type).
function SegBtn({ active, onClick, children, activeColor = C.teal }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: 1, background: active ? activeColor : 'none',
      border: `1px solid ${active ? activeColor : C.border}`, borderRadius: 8,
      color: active ? C.bg : C.muted, fontSize: 13, fontWeight: active ? 600 : 400,
      padding: '10px', minHeight: 44, cursor: 'pointer'
    }}>{children}</button>
  );
}

// Consistent text/select/date/time input — padding 10px, fontSize 14,
// borderRadius 8, minHeight 44, dark bg, 1px border (matches Students.js Field).
const inputStyle = {
  padding: '10px', background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 8, color: C.text, fontSize: 14, minHeight: 44, boxSizing: 'border-box'
};

// ─── date helpers (all in Irish time) ──────────────────────────────────────

function mondayOf(date) {
  const irishStr = date.toLocaleDateString('en-CA', { timeZone: TZ }); // YYYY-MM-DD
  const parts = irishStr.split('-').map(Number);
  const local = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  const jsDay = local.getUTCDay();              // 0=Sun..6=Sat
  const offset = (jsDay === 0 ? 6 : jsDay - 1); // days since Monday
  local.setUTCDate(local.getUTCDate() - offset);
  return local;
}

function addDays(date, n) {
  const x = new Date(date);
  x.setUTCDate(x.getUTCDate() + n);
  return x;
}

function ymd(date) {
  return date.toISOString().slice(0, 10);
}

function dayNum(date) {
  return String(Number(ymd(date).split('-')[2]));
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString('en-IE', {
    timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false
  });
}

function minutesIntoDay(iso) {
  const hhmm = new Date(iso).toLocaleTimeString('en-GB', {
    timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false
  });
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function irishDateOf(iso) {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: TZ });
}

// Every YYYY-MM-DD between two date strings, inclusive. Used to expand a
// time-off range into one blocked_days row per day.
function datesInRange(fromYmd, toYmd) {
  const out = [];
  let cur = new Date(fromYmd + 'T12:00:00Z');
  const end = new Date(toYmd + 'T12:00:00Z');
  while (cur <= end) {
    out.push(cur.toISOString().slice(0, 10));
    cur = new Date(cur.getTime() + 86400000);
  }
  return out;
}

// ─── main ───────────────────────────────────────────────────────────────────

export default function Diary({ instructor }) {
  const isMobile = useIsMobile();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [bookings, setBookings]   = useState(null);
  const [blocked, setBlocked]     = useState([]); // [{ blocked_date, reason }]
  const [selected, setSelected]   = useState(null); // tapped booking (detail panel)
  const [names, setNames]         = useState({});
  const [busy, setBusy]           = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false); // two-step cancel
  const [actionNote, setActionNote]             = useState(null);  // result message
  // Move state
  const [moving, setMoving]       = useState(false);
  const [moveDate, setMoveDate]   = useState('');
  const [moveTime, setMoveTime]   = useState('');
  // Time-off panel state
  const [timeOffOpen, setTimeOffOpen] = useState(false);
  const [toFrom, setToFrom]           = useState('');
  const [toTo, setToTo]               = useState('');
  const [toReason, setToReason]       = useState('Holiday');
  const [toNote, setToNote]           = useState(null);
  // Add-lesson panel state
  const [addOpen, setAddOpen]         = useState(false);
  const [addStudents, setAddStudents] = useState([]); // [{ wa_id, name }]
  const [addWaId, setAddWaId]         = useState('');
  const [addDate, setAddDate]         = useState('');
  const [addTime, setAddTime]         = useState('');
  const [addType, setAddType]         = useState('lesson'); // 'lesson' | 'test'
  const [addNote, setAddNote]         = useState(null);
  const [addLocation, setAddLocation] = useState('');

  const gridStartMin = Math.max(0,    (instructor.start_hour - 1) * 60 + (instructor.start_minute || 0));
  const gridEndMin   = Math.min(1440, (instructor.end_hour   + 1) * 60 + (instructor.end_minute   || 0));
  const PX_PER_MIN = 0.9;
  const gridHeight = (gridEndMin - gridStartMin) * PX_PER_MIN;

  const daysOff = useMemo(
    () => new Set((instructor.days_off || '').toUpperCase().split(',').map(s => s.trim()).filter(Boolean)
      .map(s => s.slice(0, 3))),
    [instructor.days_off]
  );

  const dayEndOverrides = useMemo(() => {
    try { return instructor.day_end_overrides ? JSON.parse(instructor.day_end_overrides) : {}; }
    catch { return {}; }
  }, [instructor.day_end_overrides]);

  const weekDays = useMemo(
    () => DAY_KEYS.map((key, i) => {
      const date = addDays(weekStart, i);
      return { key, label: DAY_LABELS[key], date, ymd: ymd(date) };
    }),
    [weekStart]
  );

  // Blocked dates as a quick lookup map { 'YYYY-MM-DD': reason }.
  const blockedMap = useMemo(() => {
    const m = {};
    for (const b of blocked) m[b.blocked_date] = b.reason || 'Time off';
    return m;
  }, [blocked]);

  const load = useCallback(async () => {
    const startIso = weekStart.toISOString();
    const endIso   = addDays(weekStart, 7).toISOString();
    const startYmd = irishDateOf(startIso);
    const endYmd   = irishDateOf(endIso);

    const { data } = await supabase
      .from('bookings')
      .select('booking_id, wa_id, start_iso, end_iso, status, lesson_type, car_used, is_edt, edt_lesson_number, payment_state, deposit_amount, location')
      .eq('instructor_key', instructor.instructor_key)
      .eq('status', 'booked')
      .eq('archived', false)
      .gte('start_iso', startIso)
      .lt('start_iso', endIso)
      .order('start_iso', { ascending: true });

    // Blocked (time-off) days for the visible week.
    const { data: blk } = await supabase
      .from('blocked_days')
      .select('blocked_date, reason')
      .eq('instructor_key', instructor.instructor_key)
      .gte('blocked_date', startYmd)
      .lte('blocked_date', endYmd);

    const waIds = [...new Set((data || []).map(b => b.wa_id))];
    const nameMap = {};
    if (waIds.length) {
      const { data: studs } = await supabase
        .from('students').select('wa_id, name').in('wa_id', waIds);
      for (const s of (studs || [])) nameMap[s.wa_id] = s.name;
    }

    setNames(nameMap);
    setBookings(data || []);
    setBlocked(blk || []);
  }, [instructor.instructor_key, weekStart]);

  useEffect(() => { load(); }, [load]);

  function openLesson(b) {
    setConfirmingCancel(false);
    setActionNote(null);
    setMoving(false);
    setMoveDate(irishDateOf(b.start_iso));
    setMoveTime(fmtTime(b.start_iso));
    setSelected(b);
  }
  function closePanel() {
    setConfirmingCancel(false);
    setActionNote(null);
    setMoving(false);
    setSelected(null);
  }

  // ── Time-off panel ──
  function openTimeOff() {
    const today = irishDateOf(new Date().toISOString());
    setToFrom(today);
    setToTo(today);
    setToReason('Holiday');
    setToNote(null);
    setTimeOffOpen(true);
  }
  function closeTimeOff() {
    setTimeOffOpen(false);
    setToNote(null);
  }

  // How many booked lessons fall in the chosen range — so we can warn that
  // blocking doesn't cancel them (it only stops NEW bookings).
  const lessonsInRange = useMemo(() => {
    if (!timeOffOpen || !toFrom || !toTo || !bookings) return 0;
    const days = new Set(datesInRange(toFrom, toTo));
    return bookings.filter(b => days.has(irishDateOf(b.start_iso))).length;
  }, [timeOffOpen, toFrom, toTo, bookings]);

  async function confirmTimeOff() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(toFrom) || !/^\d{4}-\d{2}-\d{2}$/.test(toTo)) {
      return setToNote({ type: 'err', text: 'Pick a start and end date.' });
    }
    if (toTo < toFrom) {
      return setToNote({ type: 'err', text: 'The end date is before the start date.' });
    }

    setBusy(true);
    setToNote(null);

    const days = datesInRange(toFrom, toTo);

    // Don't double-insert days that are already blocked.
    const existing = new Set(blocked.map(b => b.blocked_date));
    const rows = days
      .filter(d => !existing.has(d))
      .map(d => ({
        instructor_key: instructor.instructor_key,
        blocked_date:   d,
        reason:         toReason
      }));

    if (rows.length === 0) {
      setBusy(false);
      return setToNote({ type: 'err', text: 'Those days are already marked as time off.' });
    }

    const { error } = await supabase.from('blocked_days').insert(rows);
    setBusy(false);

    if (error) return setToNote({ type: 'err', text: error.message });

    await load();
    closeTimeOff();
  }

  // Remove a single day's block (reopens it to booking).
  async function unblockDay(dateYmd) {
    setBusy(true);
    await supabase
      .from('blocked_days')
      .delete()
      .eq('instructor_key', instructor.instructor_key)
      .eq('blocked_date', dateYmd);
    await load();
    setBusy(false);
  }

  // ── Add-lesson panel ──
  // Loads the instructor's bookable students for the dropdown. Defaults the date
  // to TODAY (most common case for a manual add — a short-notice or pretest
  // lesson is almost always near-term), time blank.
  async function openAddLesson() {
    const { data: studs } = await supabase
      .from('students')
      .select('wa_id, name, status, approval_status')
      .eq('instructor_key', instructor.instructor_key)
      .order('name', { ascending: true });

    // Bookable students only: approved and not blocked/archived.
    const list = (studs || []).filter(s =>
      s.approval_status !== 'pending' && s.approval_status !== 'declined' &&
      s.status !== 'blocked' && s.status !== 'archived'
    );

    setAddStudents(list);
    setAddWaId(list[0]?.wa_id || '');
    setAddDate(irishDateOf(new Date().toISOString())); // default to today
    setAddTime('');
    setAddType('lesson');
    setAddLocation('');
    setAddNote(null);
    setAddOpen(true);
  }
  function closeAddLesson() {
    setAddOpen(false);
    setAddNote(null);
  }

  // Soft warning if the chosen slot isn't a normally-bookable time (outside
  // hours, day off, blocked). NOT a block — the instructor has full control —
  // just a heads-up so a mistyped time doesn't slip in unnoticed.
  function addSlotWarning() {
    if (!addDate || !/^\d{2}:\d{2}$/.test(addTime)) return null;
    const dayKey = DAY_KEYS[(new Date(addDate + 'T12:00:00Z').getUTCDay() + 6) % 7];
    if (daysOff.has(dayKey)) return 'That day is normally a day off.';
    if (blockedMap[addDate] !== undefined) return `That day is marked as time off (${blockedMap[addDate]}).`;
    const [h, m] = addTime.split(':').map(Number);
    const mins = h * 60 + m;
    const startMin = instructor.start_hour * 60 + (instructor.start_minute || 0);
    const endMin   = instructor.end_hour * 60 + (instructor.end_minute || 0);
    if (mins < startMin || mins >= endMin) return 'That time is outside your normal working hours.';
    return null;
  }

  async function confirmAddLesson() {
    if (!addWaId) return setAddNote({ type: 'err', text: 'Pick a student.' });
    if (!/^\d{4}-\d{2}-\d{2}$/.test(addDate) || !/^\d{2}:\d{2}$/.test(addTime)) {
      return setAddNote({ type: 'err', text: 'Pick a date and time.' });
    }
    setBusy(true);
    setAddNote(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/add-lesson`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ wa_id: addWaId, date: addDate, time: addTime, lesson_type: addType, location: addLocation || undefined })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAddNote({ type: 'err', text: body.error || 'Could not add the lesson.' });
        setBusy(false);
        return;
      }
      await load();
      setBusy(false);
      closeAddLesson();
    } catch {
      setAddNote({ type: 'err', text: 'Network error — please try again.' });
      setBusy(false);
    }
  }

  async function toggleDeposit(booking) {
    setBusy(true);
    const next = booking.payment_state === 'deposit_paid' ? 'deposit_pending' : 'deposit_paid';
    await supabase
      .from('bookings')
      .update({ payment_state: next, updated_at: new Date().toISOString() })
      .eq('booking_id', booking.booking_id);
    await load();
    setSelected(s => s ? { ...s, payment_state: next } : s);
    setBusy(false);
  }

  async function cancelLesson(booking) {
    setBusy(true);
    setActionNote(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/cancel-lesson`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ booking_id: booking.booking_id })
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        setActionNote({ type: 'err', text: body.error || 'Could not cancel the lesson.' });
        setBusy(false);
        return;
      }
      await load();
      setBusy(false);
      closePanel();
    } catch {
      setActionNote({ type: 'err', text: 'Network error — please try again.' });
      setBusy(false);
    }
  }

  function clashWarning() {
    if (!moveDate || !/^\d{2}:\d{2}$/.test(moveTime) || !bookings) return null;
    const [h, m] = moveTime.split(':').map(Number);
    const chosenMin = h * 60 + m;
    const sameWeek = irishDateOf(weekStart.toISOString()) <= moveDate
      && moveDate < irishDateOf(addDays(weekStart, 7).toISOString());
    if (!sameWeek) return null; // can't see other weeks — stay honest

    const clash = bookings.find(b =>
      b.booking_id !== (selected && selected.booking_id) &&
      irishDateOf(b.start_iso) === moveDate &&
      Math.abs(minutesIntoDay(b.start_iso) - chosenMin) < 60
    );
    if (!clash) return null;
    return `Heads up: ${names[clash.wa_id] || 'another lesson'} is already near ${moveTime} that day.`;
  }

  async function confirmMove(booking) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(moveDate) || !/^\d{2}:\d{2}$/.test(moveTime)) {
      setActionNote({ type: 'err', text: 'Pick a valid date and time first.' });
      return;
    }
    setBusy(true);
    setActionNote(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/move-lesson`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ booking_id: booking.booking_id, new_date: moveDate, new_time: moveTime })
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        setActionNote({ type: 'err', text: body.error || 'Could not move the lesson.' });
        setBusy(false);
        return;
      }
      await load();
      setBusy(false);
      closePanel();
    } catch {
      setActionNote({ type: 'err', text: 'Network error — please try again.' });
      setBusy(false);
    }
  }

  const todayYmd = irishDateOf(new Date().toISOString());
  const monthLabel = weekStart.toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' });

  const hourLines = [];
  for (let mins = Math.ceil(gridStartMin / 60) * 60; mins <= gridEndMin; mins += 60) {
    hourLines.push(mins);
  }

  const warning = clashWarning();

  return (
    <div style={{ fontFamily: 'Poppins, sans-serif' }}>
      {/* Header: month + add actions + week nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{monthLabel}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Btn onClick={openAddLesson} color={C.teal} border={C.teal}>+ Lesson</Btn>
          <Btn onClick={openTimeOff} color={C.amber} border={C.amber}>+ Time off</Btn>
          <NavBtn onClick={() => setWeekStart(addDays(weekStart, -7))}>‹</NavBtn>
          <NavBtn onClick={() => setWeekStart(mondayOf(new Date()))}>Now</NavBtn>
          <NavBtn onClick={() => setWeekStart(addDays(weekStart, 7))}>›</NavBtn>
        </div>
      </div>

      {bookings === null ? (
        <div style={{ color: C.muted, padding: 20 }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', background: C.card }}>
          {/* Time gutter */}
          <div style={{ width: 46, flexShrink: 0, borderRight: `1px solid ${C.border}`, position: 'relative', paddingTop: 44 }}>
            <div style={{ position: 'relative', height: gridHeight }}>
              {hourLines.map(mins => (
                <div key={mins} style={{
                  position: 'absolute', top: (mins - gridStartMin) * PX_PER_MIN - 7,
                  right: 6, fontSize: 10, color: C.muted
                }}>
                  {String(Math.floor(mins / 60)).padStart(2, '0')}:00
                </div>
              ))}
            </div>
          </div>

          {/* Day columns */}
          <div style={{ display: 'flex', flex: 1, overflowX: 'auto' }}>
            {weekDays.map(day => {
              const isOff      = daysOff.has(day.key);
              const isToday    = day.ymd === todayYmd;
              const blockReason = blockedMap[day.ymd];        // instructor time-off
              const isBlocked   = blockReason !== undefined;
              const dayBookings = bookings.filter(b => irishDateOf(b.start_iso) === day.ymd);

              const overrideEnd = dayEndOverrides[day.key];
              let shadeFromMin = null;
              if (overrideEnd) {
                const [h, m] = overrideEnd.split(':').map(Number);
                shadeFromMin = h * 60 + m;
              }

              return (
                <div key={day.key} style={{
                  flex: 1, minWidth: 92, borderRight: `1px solid ${C.border}`,
                  background: isOff ? C.offBg : 'transparent'
                }}>
                  <div style={{
                    height: 44, borderBottom: `1px solid ${C.border}`, textAlign: 'center',
                    paddingTop: 6, boxSizing: 'border-box',
                    background: isToday ? C.todayBg : 'transparent'
                  }}>
                    <div style={{ fontSize: 11, color: isOff ? '#3d5a5f' : C.muted }}>{day.label}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: isToday ? C.teal : (isOff ? '#3d5a5f' : C.text) }}>
                      {dayNum(day.date)}
                    </div>
                  </div>

                  <div style={{ position: 'relative', height: gridHeight }}>
                    {hourLines.map(mins => (
                      <div key={mins} style={{
                        position: 'absolute', left: 0, right: 0,
                        top: (mins - gridStartMin) * PX_PER_MIN,
                        borderTop: '1px solid #0c2c31'
                      }} />
                    ))}

                    {isOff && (
                      <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(45deg,#081f22,#081f22 6px,#0a282c 6px,#0a282c 12px)' }} />
                    )}

                    {!isOff && shadeFromMin != null && shadeFromMin < gridEndMin && (
                      <div style={{
                        position: 'absolute', left: 0, right: 0,
                        top: Math.max(0, (shadeFromMin - gridStartMin) * PX_PER_MIN),
                        bottom: 0, background: C.offBg, opacity: 0.7
                      }} />
                    )}

                    {/* Instructor time-off overlay — sits above the grid, below lessons.
                        Tappable to remove. Amber-tinted hatching to read "blocked by me". */}
                    {isBlocked && (
                      <div
                        onClick={() => unblockDay(day.ymd)}
                        title={`${blockReason} — tap to remove`}
                        style={{
                          position: 'absolute', inset: 0, cursor: 'pointer',
                          background: 'repeating-linear-gradient(45deg,#2a1f12,#2a1f12 6px,#33260f 6px,#33260f 12px)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          gap: 4, padding: 4, textAlign: 'center'
                        }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: C.amber }}>{blockReason}</span>
                        <span style={{ fontSize: 9, color: C.muted }}>tap to remove</span>
                      </div>
                    )}

                    {dayBookings.map(b => {
                      const s = minutesIntoDay(b.start_iso);
                      const e = minutesIntoDay(b.end_iso);
                      const top = (s - gridStartMin) * PX_PER_MIN;
                      const h   = Math.max(18, (e - s) * PX_PER_MIN - 2);
                      const isEdt = b.is_edt;
                      const isTest = (b.lesson_type || '').toLowerCase() === 'test';
                      const depositPending = b.payment_state === 'deposit_pending';
                      return (
                        <div key={b.booking_id}
                          onClick={() => openLesson({ ...b, name: names[b.wa_id] || b.wa_id })}
                          style={{
                            position: 'absolute', left: 3, right: 3, top, height: h,
                            background: isEdt ? C.edtBg : C.lesBg,
                            border: `1px solid ${isEdt ? C.edtBorder : C.lesBorder}`,
                            borderRadius: 7, padding: '3px 5px', cursor: 'pointer',
                            overflow: 'hidden', boxSizing: 'border-box',
                            zIndex: 2  // lessons sit above the time-off overlay
                          }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
                            {names[b.wa_id] || 'Student'}
                            {depositPending && <span title="Deposit pending" style={{ color: C.amber }}> €</span>}
                          </div>
                          <div style={{ fontSize: 9, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
                            {fmtTime(b.start_iso)}
                            {isTest && <span style={{ color: C.tealSoft, fontWeight: 600 }}> · Pretest</span>}
                            {isEdt && b.edt_lesson_number ? ` · EDT ${b.edt_lesson_number}` : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Time-off panel ── */}
      {timeOffOpen && (
        <div onClick={closeTimeOff} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 200
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: '18px 18px 0 0',
            padding: 24, width: '100%', maxWidth: 480, fontFamily: 'Poppins, sans-serif'
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: C.text }}>Add time off</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
              Blocks these days so students can't book them. Your existing lessons stay — this only stops new bookings.
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>From</label>
                <input type="date" value={toFrom} onChange={e => { setToFrom(e.target.value); setToNote(null); }}
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>To</label>
                <input type="date" value={toTo} onChange={e => { setToTo(e.target.value); setToNote(null); }}
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
            </div>

            <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 6 }}>Reason</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {REASONS.map(r => (
                <SegBtn key={r} active={toReason === r} onClick={() => setToReason(r)} activeColor={C.amber}>
                  {r}
                </SegBtn>
              ))}
            </div>

            {lessonsInRange > 0 && (
              <div style={{
                marginBottom: 14, padding: '9px 11px', borderRadius: 8, fontSize: 13,
                background: C.amber + '22', border: `1px solid ${C.amber}`, color: C.amber
              }}>
                ⚠ You've {lessonsInRange} lesson{lessonsInRange > 1 ? 's' : ''} booked in this range. Blocking won't cancel {lessonsInRange > 1 ? 'them' : 'it'} — cancel separately if you need to.
              </div>
            )}

            {toNote && (
              <div style={{
                marginBottom: 14, padding: '8px 10px', borderRadius: 8, fontSize: 13,
                background: toNote.type === 'err' ? C.redBg : C.greenBg,
                color: toNote.type === 'err' ? C.red : C.green
              }}>
                {toNote.text}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <Btn onClick={confirmTimeOff} disabled={busy} fill filled bg={C.amber} color={C.bg}>
                {busy ? 'Saving…' : 'Block these days'}
              </Btn>
              <Btn onClick={closeTimeOff} disabled={busy} fill filled bg={C.card2} color={C.text}>
                Cancel
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Add-lesson panel ── */}
      {addOpen && (() => {
        const w = addSlotWarning();
        return (
          <div onClick={closeAddLesson} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 200
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: C.card, border: `1px solid ${C.border}`, borderRadius: '18px 18px 0 0',
              padding: 24, width: '100%', maxWidth: 480, fontFamily: 'Poppins, sans-serif'
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: C.text }}>Add a lesson</div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                For a lesson you've arranged directly — a pretest, or a short-notice slot. Goes straight into your diary and calendar.
              </div>

              {addStudents.length === 0 ? (
                <div style={{ fontSize: 14, color: C.muted, padding: '8px 0 16px' }}>
                  No active students yet to add a lesson for.
                </div>
              ) : (
                <>
                  <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>Student</label>
                  <select value={addWaId} onChange={e => { setAddWaId(e.target.value); setAddNote(null); }}
                    style={{ ...inputStyle, width: '100%', marginBottom: 14 }}>
                    {addStudents.map(s => (
                      <option key={s.wa_id} value={s.wa_id}>{s.name || s.wa_id}</option>
                    ))}
                  </select>

                  <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>Date</label>
                      <input type="date" value={addDate} onChange={e => { setAddDate(e.target.value); setAddNote(null); }}
                        style={{ ...inputStyle, width: '100%' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>Time</label>
                      <input type="time" value={addTime} onChange={e => { setAddTime(e.target.value); setAddNote(null); }}
                        style={{ ...inputStyle, width: '100%' }} />
                    </div>
                  </div>

                  <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 6 }}>Type</label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    {[['lesson', 'Lesson'], ['test', 'Pretest']].map(([val, lbl]) => (
                      <SegBtn key={val} active={addType === val} onClick={() => setAddType(val)}>
                        {lbl}
                      </SegBtn>
                    ))}
                  </div>

                  {(instructor.locations || []).length > 1 && (
                    <>
                      <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>Pickup location</label>
                      <select value={addLocation} onChange={e => { setAddLocation(e.target.value); setAddNote(null); }}
                        style={{ ...inputStyle, width: '100%', marginBottom: 14 }}>
                        <option value="">Pick a location…</option>
                        {instructor.locations.map((loc, i) => (
                          <option key={i} value={loc}>{loc}</option>
                        ))}
                      </select>
                    </>
                  )}

                  {w && (
                    <div style={{
                      marginBottom: 14, padding: '9px 11px', borderRadius: 8, fontSize: 13,
                      background: C.amber + '22', border: `1px solid ${C.amber}`, color: C.amber
                    }}>
                      ⚠ {w} You can still add it — just confirming you meant to.
                    </div>
                  )}

                  {addNote && (
                    <div style={{
                      marginBottom: 14, padding: '8px 10px', borderRadius: 8, fontSize: 13,
                      background: addNote.type === 'err' ? C.redBg : C.greenBg,
                      color: addNote.type === 'err' ? C.red : C.green
                    }}>
                      {addNote.text}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 10 }}>
                    <Btn onClick={confirmAddLesson} disabled={busy} fill filled bg={C.teal} color={C.bg}>
                      {busy ? 'Adding…' : 'Add lesson'}
                    </Btn>
                    <Btn onClick={closeAddLesson} disabled={busy} fill filled bg={C.card2} color={C.text}>
                      Cancel
                    </Btn>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Lesson detail panel */}
      {selected && (
        <div onClick={closePanel} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 200
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: '18px 18px 0 0',
            padding: 24, width: '100%', maxWidth: 480, fontFamily: 'Poppins, sans-serif'
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: C.text }}>{selected.name}</div>
            <DetailRow label="Date"  value={new Date(selected.start_iso).toLocaleDateString('en-GB', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' })} />
            <DetailRow label="Time"  value={`${fmtTime(selected.start_iso)} – ${fmtTime(selected.end_iso)}`} />
            <DetailRow label="Type"  value={(selected.lesson_type || '').toLowerCase() === 'test' ? 'Pretest' : 'Lesson'} />
            <DetailRow label="Car"   value={selected.car_used === 'student' ? 'Own car' : 'Your car'} />
            <DetailRow label="EDT"   value={selected.is_edt ? `Lesson ${selected.edt_lesson_number || '—'}` : 'Not marked as EDT'} />
            {selected.location && <DetailRow label="Location" value={selected.location} />}

            {(selected.payment_state === 'deposit_pending' || selected.payment_state === 'deposit_paid') && (
              <div style={{
                display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center',
                gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.border}`
              }}>
                <span style={{ fontSize: 13, color: C.muted }}>Deposit</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: selected.payment_state === 'deposit_paid' ? C.green : C.amber }}>
                    €{selected.deposit_amount || '—'} {selected.payment_state === 'deposit_paid' ? 'paid' : 'pending'}
                  </span>
                  {selected.payment_state === 'deposit_paid' ? (
                    <Btn onClick={() => toggleDeposit(selected)} disabled={busy} color={C.muted} border={C.border} fill={isMobile}>
                      Undo
                    </Btn>
                  ) : (
                    <Btn onClick={() => toggleDeposit(selected)} disabled={busy} filled bg={C.green} color={C.greenBg} fill={isMobile}>
                      Mark paid
                    </Btn>
                  )}
                </div>
              </div>
            )}

            {actionNote && (
              <div style={{
                marginTop: 14, padding: '8px 10px', borderRadius: 8, fontSize: 13,
                background: actionNote.type === 'err' ? C.redBg : C.greenBg,
                color: actionNote.type === 'err' ? C.red : C.green
              }}>
                {actionNote.text}
              </div>
            )}

            {/* Move — reveals a day+time picker (free pick + soft clash warning) */}
            {!confirmingCancel && (
              <div style={{ marginTop: 18 }}>
                {!moving ? (
                  <Btn onClick={() => { setMoving(true); setActionNote(null); }} disabled={busy}
                    full color={C.tealSoft} border={C.teal}>
                    Move lesson
                  </Btn>
                ) : (
                  <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Move to a new day & time</div>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                      <input type="date" value={moveDate} onChange={e => setMoveDate(e.target.value)}
                        style={{ ...inputStyle, flex: 1 }} />
                      <input type="time" value={moveTime} onChange={e => setMoveTime(e.target.value)}
                        style={{ ...inputStyle, width: 110 }} />
                    </div>
                    {warning && (
                      <div style={{ fontSize: 12, color: C.amber, marginBottom: 10 }}>⚠ {warning}</div>
                    )}
                    <div style={{ display: 'flex', gap: 10 }}>
                      <Btn onClick={() => confirmMove(selected)} disabled={busy} fill filled bg={C.teal} color={C.bg}>
                        {busy ? 'Moving…' : 'Confirm move'}
                      </Btn>
                      <Btn onClick={() => setMoving(false)} disabled={busy} fill filled bg={C.card2} color={C.text}>
                        Cancel
                      </Btn>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Cancel — two-step confirm (hidden while moving) */}
            {!moving && (
              <div style={{ marginTop: 12 }}>
                {!confirmingCancel ? (
                  <Btn onClick={() => setConfirmingCancel(true)} disabled={busy}
                    full color={C.red} border={C.redBorder}>
                    Cancel this lesson
                  </Btn>
                ) : (
                  <div>
                    <div style={{ fontSize: 13, color: C.text, marginBottom: 10, textAlign: 'center' }}>
                      Cancel this lesson? The student will be notified.
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <Btn onClick={() => cancelLesson(selected)} disabled={busy} fill filled bg={C.red} color="#fff">
                        {busy ? 'Cancelling…' : 'Yes, cancel'}
                      </Btn>
                      <Btn onClick={() => setConfirmingCancel(false)} disabled={busy} fill filled bg={C.card2} color={C.text}>
                        Keep it
                      </Btn>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <Btn onClick={closePanel} full filled bg={C.card2} color={C.text}>Close</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NavBtn({ children, onClick }) {
  return <Btn onClick={onClick} color={C.text} border={C.border}>{children}</Btn>;
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #10434a' }}>
      <span style={{ fontSize: 13, color: '#7fb8c0' }}>{label}</span>
      <span style={{ fontSize: 13, color: '#e0f0f2', fontWeight: 600 }}>{value}</span>
    </div>
  );
}